import { Badge, Box, Button, Code, Divider, Text } from '@mantine/core'
import { JsonViewer } from '@textea/json-viewer'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import { ArrowRight } from 'tabler-icons-react'
import { AccessGrantedWebhookPayload } from '../../../../types/global'

const accessGrantedExample = {
    "id": "ccXFZzXwyB35IXNFMW6N",
    "client": {
        "owner": "24ipCCNhemAK07ZIXpOD",
        "website": "ethancrabb.com",
        "created": 1718195774391,
        "name": "Ethan Crabb Developing LTD",
        "company": "Ethan Crabb Developing",
        "type": "Client",
        "email": "mail@ethancrabb.com",
        "usingFavicon": true,
        "pfp": "https://ethancrabb.com/favicon.ico",
        "intakeForm": {
            "example_question": "Example answer"
        },
        connectedAccounts: {
            Google: {
                "Google Ads": [
                    {
                        accessLevel: "Admin",
                        inviteRef: "ccXFZzXwyB35IXNFMW6N",
                        connectionCreated: Date.now(),
                        id: "google_ads_account_id",
                        label: "Ethan's Ad Account"
                    }
                ]
            }
        },
        "id": "HfzOzr7rAE0YtZBPZ28I"
    },
    accessGranted: {
        Google: {
            "Google Ads": [
                {
                    accessLevel: "Admin",
                    inviteRef: "ccXFZzXwyB35IXNFMW6N",
                    connectionCreated: Date.now(),
                    id: "google_ads_account_id",
                    label: "Ethan's Ad Account"
                }
            ]
        }
    }
}

const clientCreatedExample = {
    "owner": "24ipCCNhemAK07ZIXpOD",
    "website": "ethancrabb.com",
    "created": 1718195774391,
    "name": "Ethan Crabb Developing LTD",
    "company": "Ethan Crabb Developing",
    "type": "Client",
    "email": "mail@ethancrabb.com",
    "usingFavicon": true,
    "pfp": "https://ethancrabb.com/favicon.ico",
    "intakeForm": {
        "example_question": "Example answer"
    },
    "id": "HfzOzr7rAE0YtZBPZ28I"
}

const Property = ({ title, desc, isNew }: { title: string, desc: string, isNew?: boolean }) => {
    return (
        <>
            <Divider opacity={.5} />
            <Box>
                <Box className='flex aic' style={{ gap: 5 }}>
                    <Text><Code fz={"sm"}>{title}</Code></Text>
                    {isNew && <Badge color='red' size='sm'>New</Badge>}
                </Box>
                <Text fz={"sm"} mt={8}>{desc}</Text>
            </Box>
        </>
    )
}

export default function ZapierDocs() {
    return (
        <Box>
            <Box style={{ width: "100%", height: 150 }} bg={"#fd5001"} className='flex aic jcc'>
                <Box className='flex aic' style={{ gap: 30 }}>
                    <Box bg="#fff" style={{ borderRadius: 10 }} w={60} h={60} className='flex aic jcc'>
                        <Image src={"/images/logos/zapier.png"} width={50} height={50} alt='Zapier Logo' />
                    </Box>
                    <Box bg="#fff" style={{ borderRadius: 10 }} w={60} h={60} className='flex aic jcc'>
                        <Image src={"/images/logo_mini.png"} width={50} height={50} alt='Zapier Logo' />
                    </Box>
                </Box>
            </Box>
            <Box p={20} maw={900} mx={"auto"}>
                <Badge color="gray" variant="light">Docs</Badge>
                <article>
                    <Text component={"h1"} fw={800} fz={30}>Using Zapier with AgencyAccess</Text>
                    <Box mt={20}>
                        <section>
                            <Text>Add AgencyAccess to your business's automated workflow with our Zapier integration. In this doc, we'll walk you through the AgencyAccess Zapier API.</Text>
                        </section>
                        <br />
                        <section>
                            <Text fw={600} fz={"xl"} component={"h2"}>Authentication</Text>
                            <Text mt={10}>
                                To start building your workflow, you'll need to authenticate using your API key.
                                You can find your agency's API key in <Text component={Link} href={"/dashboard/settings/general"} c={"blue"} span>General Settings</Text>.
                                Never share your API key with platforms you don't trust or other people. Only agency admins can view your agency's API key.
                                <br />
                                <br />
                                Once you've found your API key, copy it and paste it into the Zapier authentication screen.
                            </Text>
                        </section>
                        <br />
                        <br />

                        <section>
                            <Text component={"h2"} fw={600} fz={"xl"}>Access Granted Trigger</Text>
                            <Text mt={10}>
                                The <Code>access_granted</Code> trigger will run when a client has successfully completed onboarding by granting access to all of the requested services and filling out the intake form (if applicable).
                                The following payload will be sent to Zapier when this trigger fires:
                            </Text>
                            <Box mt={10} w={"fit-content"} p={"sm"} py={"xs"} bg={"gray.0"} className="cp" style={{ borderRadius: 10, border: "1px solid rgba(0,0,0,.1)", transition: "all .2s" }}>
                                <JsonViewer
                                    style={{ fontSize: 14 }}
                                    rootName={false}
                                    value={accessGrantedExample}
                                />
                            </Box>
                            <Box className='flex fdc' style={{ gap: 10 }} mt={15}>
                                <Text fw={500} >Properties</Text>
                                <Property title='id' desc='The ID of the access link' />
                                <Property title='client.owner' desc='The agency that owns this client (this will always be your agency ID)' />
                                <Property title='client.website' desc='The website URL of the client' />
                                <Property title="client.created" desc='The creation time of the client in ISO format' />
                                <Property title='client.name' desc='The name of the client' />
                                <Property title='client.company' desc="The client's company name" />
                                <Property title='client.type' desc="The client's type. This is either `Client` or `Prospect`" />
                                <Property title='client.email' desc="The client's email address" />
                                <Property title='client.usingFavicon' desc="If the client's profile picture is their website favicon, this will be `true`. Otherwise, this will be `false`" />
                                <Property title='client.pfp' desc="A link to the client's profile picture" />
                                <Property title='client.intakeForm' desc="This property holds all of the client's intake form answers in key-value format. You can find the key for your intake form question in intake form settings." />
                                <Property title='client.connectedAccounts' desc="This object holds all the the client's connected accounts accross all access links" />
                                <Property title='client.id' desc='The ID of the client' />
                                <Property isNew title='accessGranted' desc="This object holds the accounts that the client has connected using this access link. It's structured the same as client.connectedAccounts, but only includes accounts connected using this access link." />
                            </Box>
                        </section>
                        <br />
                        <br />

                        <section>
                            <Text component={"h2"} fw={600} fz={"xl"}>Client Created Trigger</Text>
                            <Text mt={10}>
                                The <Code>client_created</Code> trigger will run when a client is created in the dashboard, or via a static link.
                                The following payload will be sent to Zapier when this trigger fires:
                            </Text>
                            <Box mt={10} w={"fit-content"} p={"sm"} py={"xs"} bg={"gray.0"} className="cp" style={{ borderRadius: 10, border: "1px solid rgba(0,0,0,.1)", transition: "all .2s" }}>
                                <JsonViewer
                                    style={{ fontSize: 14 }}
                                    rootName={false}
                                    value={clientCreatedExample}
                                />
                            </Box>
                            <Box className='flex fdc' style={{ gap: 10 }} mt={15}>
                                <Text fw={500}>Properties</Text>
                                <Property title='owner' desc='The agency that owns this client (this will always be your agency ID)' />
                                <Property title='website' desc='The website URL of the client' />
                                <Property title="created" desc='The creation time of the client in ISO format' />
                                <Property title='name' desc='The name of the client' />
                                <Property title='company' desc="The client's company name" />
                                <Property title='type' desc="The client's type. This is either `Client` or `Prospect`" />
                                <Property title='email' desc="The client's email address" />
                                <Property title='usingFavicon' desc="If the client's profile picture is their website favicon, this will be `true`. Otherwise, this will be `false`" />
                                <Property title='pfp' desc="A link to the client's profile picture" />
                                <Property title='intakeForm' desc="This property holds all of the client's intake form answers in key-value format. You can find the key for your intake form question in intake form settings." />
                                <Property title='connectedAccounts' desc="This object holds all the the client's connected accounts accross all access links" />
                                <Property title='id' desc='The ID of the client' />
                            </Box>
                        </section>
                    </Box>
                    <Box py={15} my={30} style={{ borderRadius: 10 }} className='flex aic jcsb' mt={15} p={"sm"} bg={"linear-gradient(-90deg, rgba(0,0,0,1) 0%, var(--mantine-color-blue-6) 100%)"}>
                        <Box>
                            <Text lh={1} fw={700} c="#fff">Want more Zapier integrations?</Text>
                            <Text lh={1} mt={5} fw={500} fz="sm" c='#fff'>Send us an email with your suggestions and we'll add them to our development timeline</Text>
                        </Box>
                        <Button onClick={() => open("mailto:support@agencyaccess.co")} size="compact-sm" variant="light" color='#fff' rightSection={<ArrowRight size={17} />}>Get in Touch</Button>
                    </Box>
                </article>
            </Box>
        </Box>
    )
}
